import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  fetchCompanyPayoutDetails,
  fetchCompanyOwnerById,
  fetchMyCompany,
} from '../services/api/companyOwnerAuth';
import { fetchCustomerById } from '../services/api/customerAuth';
import { fetchDriverByEmail } from '../services/api/driverAuth';
import {
  clearCompanyOwnerAuthFlow,
  saveCompanyOwnerAuthTokens,
  saveCompanyOwnerAuthUser,
  saveCompanyOwnerCompany,
  saveCompanyOwnerPayoutDetails,
} from '../services/storage/companyOwnerFlow';
import {
  clearCustomerAuthFlow,
  saveCustomerAuthTokens,
  saveCustomerAuthUser,
} from '../services/storage/customerFlow';
import {
  clearDriverAuthFlow,
  saveDriverAuthTokens,
  saveDriverAuthUser,
} from '../services/storage/driverFlow';
import { decodeJwtPayload, nextStageForCustomer } from '../utils/customerOnboarding';
import { getCompanyOwnerNextRoute } from '../utils/companyOwner';
import { nextStageForDriver } from '../utils/driverOnboarding';
import '../styles/register.css';

function getTokenRoles(payload: Record<string, any> | null): string[] {
  if (!payload) return [];

  const directRoles = Array.isArray(payload.roles) ? payload.roles : [];
  const authorityRoles = Array.isArray(payload.authorities) ? payload.authorities : [];
  const scopeRoles = typeof payload.scope === 'string' ? payload.scope.split(' ') : [];

  return [...directRoles, ...authorityRoles, ...scopeRoles].map((value) => String(value));
}

const OAuthRedirectPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('Completing Google sign up...');

  useEffect(() => {
    const completeOAuth = async () => {
      const token = searchParams.get('accessToken') || searchParams.get('token');
      const refreshToken = searchParams.get('refreshToken');
      const accessTokenExpiresAt = searchParams.get('accessTokenExpiresAt');
      const refreshTokenExpiresAt = searchParams.get('refreshTokenExpiresAt');

      if (!token) {
        navigate('/auth/error?message=No authentication token received.', { replace: true });
        return;
      }

      try {
        const payload = decodeJwtPayload(token);
        const roles = getTokenRoles(payload);
        const isDriver = roles.includes('ROLE_DRIVER');
        const isCompanyOwner = roles.includes('ROLE_COMPANY_OWNER');

        await clearCompanyOwnerAuthFlow();
        await clearCustomerAuthFlow();
        await clearDriverAuthFlow();

        if (isCompanyOwner) {
          const userId = payload?.uid || payload?.sub || payload?.userId;

          if (!userId) {
            throw new Error('Invalid token received from Google sign up.');
          }

          saveCompanyOwnerAuthTokens({
            accessToken: token,
            refreshToken,
            accessTokenExpiresAt,
            refreshTokenExpiresAt,
          });
          setStatus('Loading your company owner account...');
          const companyOwnerUser = await fetchCompanyOwnerById(String(userId), token);
          saveCompanyOwnerAuthUser(companyOwnerUser);

          const company = await fetchMyCompany();
          if (company) {
            saveCompanyOwnerCompany(company);
            try {
              const payoutDetails = await fetchCompanyPayoutDetails();
              saveCompanyOwnerPayoutDetails(payoutDetails);
              navigate(getCompanyOwnerNextRoute(company, payoutDetails), { replace: true });
              return;
            } catch {
              // Fall back to the company route derived from the cached company record.
            }
          }

          navigate(getCompanyOwnerNextRoute(company), { replace: true });
          return;
        }

        if (isDriver) {
          const email = payload?.email || payload?.preferred_username || payload?.sub;
          if (!email || !String(email).includes('@')) {
            throw new Error('Could not identify the driver account returned from Google sign in.');
          }

          saveDriverAuthTokens({
            accessToken: token,
            refreshToken,
            accessTokenExpiresAt,
            refreshTokenExpiresAt,
          });
          setStatus('Loading your driver account...');
          const driver = await fetchDriverByEmail(String(email), token);
          saveDriverAuthUser(driver);

          const nextStage = nextStageForDriver(driver);
          navigate(
            nextStage === 'completed'
              ? '/driver/account-ready'
              : '/register?resume=oauth&role=driver',
            {
              replace: true,
            },
          );
          return;
        }

        saveCustomerAuthTokens({
          accessToken: token,
          refreshToken,
          accessTokenExpiresAt,
          refreshTokenExpiresAt,
        });

        const userId = payload?.uid || payload?.sub || payload?.userId;

        if (!userId) {
          throw new Error('Invalid token received from Google sign up.');
        }

        setStatus('Loading your account...');
        const user = await fetchCustomerById(String(userId), token);
        saveCustomerAuthUser(user);

        const nextStage = nextStageForCustomer(user);
        navigate(
          nextStage === 'completed'
            ? '/schedule-booking'
            : '/register?resume=oauth&role=customer',
          {
            replace: true,
          },
        );
      } catch (error: any) {
        navigate(
          `/auth/error?message=${encodeURIComponent(
            error.message || 'Failed to complete Google sign up.',
          )}`,
          { replace: true },
        );
      }
    };

    void completeOAuth();
  }, [navigate, searchParams]);

  return (
    <section className="register-page">
      <div className="register-shell register-shell-single">
        <div className="register-panel register-panel-form register-status-panel">
          <div className="register-loader" />
          <h1 className="register-form-title">Please wait</h1>
          <p className="register-form-copy">{status}</p>
        </div>
      </div>
    </section>
  );
};

export default OAuthRedirectPage;
